import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    Table,
    TableHeader,
    TableBody,
    TableRow,
    TableHead,
    TableCell,
} from '../../../../components/ui/Table';
import Pagination from '../../../../components/Pagination';
import ReportsController from '../../../../components/reportsController';
import { WorkSessionReportView } from '../../../../models/projectManagement';
import * as ReportsApi from '../../../../api/Attendance/reports';
import { FetchUsers } from '../../../../api/General/account';
import { getBranches, getDepartments } from '../../../../api/General/Company';
import { getCountries } from '../../../../api/General/Country';
import { getProjects } from '../../../../api/ProjectManagement/project';
import { formatDateTime, formatDuration } from '../../../../utils';

export default function WorkSessions() {
    const { t } = useTranslation();
    const { userId, company } = useSelector((state: any) => ({
        userId: state.user.userId,
        company: state.user.company,
    }));

    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState<any[]>([]);
    const [count, setCount] = useState(0);
    const [offset, setOffset] = useState(0);

    const [users, setUsers] = useState<any[]>([]);
    const [users_, setUsers_] = useState<any[]>([]);
    const [selectedUsers, setSelectedUsers] = useState<any[]>([]);

    const [countries, setCountries] = useState<any[]>([]);
    const [selectedCountries, setSelectedCountries] = useState<any[]>([]);
    const [branches, setBranches] = useState<any[]>([]);
    const [selectedBranches, setSelectedBranches] = useState<any[]>([]);
    const [departments, setDepartments] = useState<any[]>([]);
    const [selectedDepartments, setSelectedDepartments] = useState<any[]>([]);
    const [projects, setProjects] = useState<any[]>([]);
    const [selectedProjects, setSelectedProjects] = useState<any[]>([]);

    const [showFilters, setShowFilters] = useState(false);
    const [searchMethod, setSearchMethodState] = useState('AND');

    const now = new Date();
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(now.getDate() - 7);
    const oneDayAhead = new Date();
    oneDayAhead.setDate(now.getDate() + 1);

    const [dateFrom, setDateFrom] = useState<Date>(oneWeekAgo);
    const [dateTo, setDateTo] = useState<Date>(oneDayAhead);

    const columns = useMemo(
        () => [
            {
                key: 'name',
                name: t('Name'),
                onRender: (item: WorkSessionReportView) => (
                    <span>{item.user?.fullName}</span>
                ),
            },
            {
                key: 'project',
                name: t('Project'),
                onRender: (item: WorkSessionReportView) => (
                    <span>{item.project ? item.project?.title : t('Not Specified')}</span>
                ),
            },
            {
                key: 'startTime',
                name: t('Start Time'),
                onRender: (item: WorkSessionReportView) => (
                    <span>{formatDateTime(new Date(item.startTime))}</span>
                ),
            },
            {
                key: 'endTime',
                name: t('End Time'),
                onRender: (item: WorkSessionReportView) => (
                    <span>
                        {item.endTime
                            ? formatDateTime(new Date(item.endTime))
                            : t('Not Available')}
                    </span>
                ),
            },
            {
                key: 'duration',
                name: t('Duration'),
                onRender: (item: WorkSessionReportView) => (
                    <span>
                        {item.duration ? formatDuration(item.duration) : t('Not Available')}
                    </span>
                ),
            },
            {
                key: 'comments',
                name: t('Comments'),
                onRender: (item: WorkSessionReportView) => (
                    <span>
                        {item.comments &&
                            `${t('Start Task')} : ${item.comments[0]} | ${t('End Task')} : ${item.comments[1]}`}
                    </span>
                ),
            },
        ],
        [t],
    );

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const input = {
                userId,
                userIds: selectedUsers.map((u: any) => u.id),
                departmentIds: selectedDepartments.map((d: any) => d.id),
                branchIds: selectedBranches.map((b: any) => b.id),
                countryIds: selectedCountries.map((c: any) => c.id),
                projectIds: selectedProjects.map((p: any) => p.id),
                searchMethod,
                from: dateFrom,
                to: dateTo,
                offset,
                limit: 10,
            };
            const response = await ReportsApi.getWorkSessionsPerUser(input);
            const payload = response.payload || [];
            setItems(payload.map((item: any, i: number) => ({ key: i, ...item })));
            setCount(payload.length);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [userId, selectedUsers, selectedDepartments, selectedBranches, selectedCountries, selectedProjects, searchMethod, dateFrom, dateTo, offset]);

    useEffect(() => {
        fetchData();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const loadFilters = async () => {
            // Users
            if (users.length === 0) {
                const usersReq: any = await FetchUsers('Company', company?.id ?? 0, false);
                if (usersReq?.payload) {
                    for (let i = 0; i < usersReq.payload.length; i++) {
                        usersReq.payload[i].header = usersReq.payload[i].fullName;
                        usersReq.payload[i].key = usersReq.payload[i].id;
                    }
                    usersReq.payload.unshift({ header: 'All Users', key: 0 });
                    setUsers(usersReq.payload);
                    setUsers_(usersReq.payload);
                }
            }
            // Branches
            const branchesRes = await getBranches(company?.id);
            setBranches(branchesRes.payload || []);
            // Departments
            const deptsRes = await getDepartments(company?.id);
            setDepartments(deptsRes.payload || []);
            // Countries
            const countriesRes = await getCountries();
            setCountries(countriesRes.payload || []);
            // Projects
            const projectsRes: any = await getProjects();
            const prjs = projectsRes.payload || [];
            for (let i = 0; i < prjs.length; i++) {
                prjs[i].header = prjs[i].title;
                prjs[i].key = prjs[i].id;
            }
            setProjects(prjs);
        };
        loadFilters();
    }, [company?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const exportReport = async (format: string) => {
        try {
            await ReportsApi.exportWorkSessionsPerUser(
                {
                    userId,
                    userIds: selectedUsers.map((u: any) => u.id),
                    departmentIds: selectedDepartments.map((d: any) => d.id),
                    branchIds: selectedBranches.map((b: any) => b.id),
                    countryIds: selectedCountries.map((c: any) => c.id),
                    searchMethod,
                    from: dateFrom,
                    to: dateTo,
                },
                format,
            );
        } catch (error) {
            console.error(error);
        }
    };

    const onDateChange = (time: any, type: string) => {
        if (type === 'from') setDateFrom(time);
        else setDateTo(time);
    };

    const onChangeUser = (_ev: any, props: any) => {
        const allSelected = props.value.find((i: any) => i.key === 0);
        if (allSelected) {
            setSelectedUsers(users.filter((u) => u.key !== 0));
        } else {
            setSelectedUsers([...props.value]);
        }
    };

    const onChangeCountry = (_ev: any, props: any) => {
        if (props.value) setSelectedCountries([...props.value]);
    };

    const onChangeBranch = (_ev: any, props: any) => {
        if (props.value) setSelectedBranches([...props.value]);
    };

    const onChangeDepartments = (_ev: any, props: any) => {
        if (props.value) setSelectedDepartments([...props.value]);
    };

    const onChangeProject = (_ev: any, props: any) => {
        if (props.value) setSelectedProjects([...props.value]);
    };

    const handlePageChange = useCallback((page: number) => {
        const newOffset = (page - 1) * 10;
        setOffset(newOffset);
    }, []);

    useEffect(() => {
        fetchData();
    }, [offset]); // eslint-disable-line react-hooks/exhaustive-deps

    const pageSize = 10;
    const totalPages = Math.max(1, Math.ceil(count / pageSize));
    const currentPage = Math.floor(offset / pageSize) + 1;

    return (
        <div className="flex flex-col gap-3">
            <ReportsController
                dateFrom={dateFrom}
                dateTo={dateTo}
                onDateChange={onDateChange}
                selectedUsers={selectedUsers}
                onChangeUser={onChangeUser}
                clearAllUsers={() => setSelectedUsers([])}
                selectedDepartments={selectedDepartments}
                onChangeDepartments={onChangeDepartments}
                clearAllDepartments={() => setSelectedDepartments([])}
                selectedBranches={selectedBranches}
                onChangeBranches={onChangeBranch}
                clearAllBranches={() => setSelectedBranches([])}
                selectedCountries={selectedCountries}
                onChangeCountry={onChangeCountry}
                clearAllCountries={() => setSelectedCountries([])}
                searchMethod={searchMethod}
                setSearchMethod={(e: any) => setSearchMethodState(e.target.value)}
                users={users}
                users_={users_}
                loading={loading}
                fetchData={fetchData}
                exportReport={exportReport}
                setShowFilters={() => setShowFilters(!showFilters)}
                showFilters={showFilters}
                countries={countries}
                branches={branches}
                departments={departments}
                projects={projects}
                selectedProjects={selectedProjects}
                onChangeProject={onChangeProject}
                clearAllProjects={() => setSelectedProjects([])}
            />

            <div className="h-px bg-border" />

            {loading ? (
                <div className="flex justify-center p-5">
                    <div className="h-8 w-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
            ) : (
                <Table aria-label="work sessions table">
                    <TableHeader>
                        <TableRow>
                            {columns.map((col) => (
                                <TableHead key={col.key}>{col.name}</TableHead>
                            ))}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {items.length > 0 ? (
                            items.map((item, index) => (
                                <TableRow key={`row-${index}`}>
                                    {columns.map((col) => (
                                        <TableCell key={`${index}-${col.key}`}>
                                            {col.onRender ? col.onRender(item) : ''}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell
                                    colSpan={columns.length}
                                    className="text-center text-muted-foreground py-8"
                                >
                                    {t('No data available')}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            )}

            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
            />
        </div>
    );
}
